<template>
    <div id="signup-frame" ref="signup-frame">
        <div class="container is-max-desktop auth-card columns">

            <div class="column left-side">
                <h1>
                    <strong>my</strong>247ed.com
                </h1>

                <section v-if="step===1">
                    <p class="mb-5 is-hidden-mobile"><strong>Welcome! </strong>Let's get you started with your free account.</p>

                    <hr/>

                    <b-message type="is-warning">
                        <div class="columns">
                            <div class="column is-narrow"><i class="far fa-fw fa-info-circle fa-2x has-text-warning"></i></div>
                            <div class="column content">
                                <h5 class="mt-2">You are signing up a new account...</h5>

                                <p>Before proceeding with the account signup, please take a moment to consider and ensure that you do not already have an existing account, possibly under a different email address. Creating a duplicate account may lead to complications in accurately tracking and synchronizing your records and certifications.</p>

                                <p class="mb-5 pb-2 has-text-warning"><i class="far fa-exclamation-circle mr-2"></i><strong class="has-text-warning">Do not sign up on the behalf of another person.</strong></p>

                                <b-collapse :open="false" animation="slide" class="message has-background-white pt-2 pl-4 pr-4 pb-2">
                                    <template #trigger="props">
                                        <div class="has-text-black">See Additional Help & Information <i class="fas fa-fw fa-chevron-down is-pulled-right"></i></div>
                                    </template>
                                    <hr/>
                                    <div class="mt-5 mb-4 has-text-grey">
                                        <h6>What if I don't have access to that email address anymore?</h6>
                                        <p>
                                            Your account and login still exists and will work.  If you don't know your password there are often other login methods you can use such as getting a code texted to your phone number.  If you need the password reset please contact us at <a href="mailto:support@my247ed.com">support@my247ed.com</a>.
                                        </p>

                                        <h6 class="mt-6">My information has changed from my old account.</h6>
                                        <p>
                                            Once logged in you can easily update your information.  No need to start over!
                                        </p>

                                        <h6 class="mt-6">I've used my247ed.com before but not for the purposes I'm here for today.</h6>
                                        <p>
                                            Understood!  We provide online courses and services for other organizations.  All your records, regardless of the purpose, are tracked in one account.  You can easily manage all your records and certifications in one place.
                                        </p>

                                        <h6 class="mt-6">What if my current account doesn't match my email address with my organization?</h6>
                                        <p>
                                            No problem! Your records are not tracked or synced by your account's email address.  Instead they are specifically associated and can be changed in the integration settings.  Creating a new account only makes it harder to properly track your records and certifications.
                                        </p>

                                        <h6 class="mt-6">I'm not sure if I have an account or not.</h6>
                                        <p>
                                            In that case please feel free to reach out to us at <a href="mailto:support@my247ed.com">support@my247ed.com</a>.  We'll be able to look up your information or suggest that you sign up.
                                        </p>
                                    </div>
                                </b-collapse>

                                <div class="mt-5 mb-4">
                                    <b-button type="is-warning" tag="router-link" to="/" icon-left="sign-in-alt" class="pl-5 pr-4 has-text-white"> &nbsp;Try to Login Instead</b-button>
                                </div>
                            </div>
                        </div>
                    </b-message>

                    <b-message type="is-info" v-if="prefilled">
                        <div class="columns">
                            <div class="column is-narrow"><i class="far fa-fw fa-memo-circle-info fa-2x"></i></div>
                            <div class="column">
                                For your convenience, some information has been prefilled from <span class="has-text-weight-bold">{{ referral }}</span>.<br>Please review all information for accuracy.
                            </div>
                        </div>
                    </b-message>

                    <section class="content mt-3">
                        <p class="mt-6"><i class="fas fa-fw fa-user mr-3"></i>Personal Information</p>

                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                            <div class="column is-2">
                                <b-field label="Title" label-position="inside">
                                    <b-select v-model="newUser.title" expanded>
                                        <option v-for="option in titleOptions" :value="option">{{ option }}</option>
                                    </b-select>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label-position="inside">
                                    <template #label>First Name <span class="required-flag">*</span></template>
                                    <b-input required v-model="newUser.first_name"></b-input>
                                </b-field>
                            </div>
                            <div class="column is-4">
                                <b-field label-position="inside">
                                    <template #label>Last Name <span class="required-flag">*</span></template>
                                    <b-input required v-model="newUser.last_name"></b-input>
                                </b-field>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                            <div class="column">
                                <b-field label-position="inside" message="(mm/dd/yyyy)">
                                    <template #label>Date of Birth <span class="required-flag">*</span></template>
                                    <b-input required v-model="newUser.dob" v-mask="'##/##/####'" placeholder="mm/dd/yyyy"></b-input>
                                </b-field>
                            </div>
                            <div class="column">
                                <gender-field v-model="newUser.gender"/>
                            </div>
                        </div>
                        <p class="mt-6"><i class="fas fa-fw fa-address-book mr-3"></i>Contact Information</p>
                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                            <div class="column is-7">
                                <b-field label-position="inside" message="Number, street, etc.">
                                    <template #label>Address <span class="required-flag">*</span></template>
                                    <b-input required v-model="newUser.address"></b-input>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label="Address 2" label-position="inside" message="Appt. Number, Suite, etc.">
                                    <b-input v-model="newUser.address_2"></b-input>
                                </b-field>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                            <div class="column">
                                <b-field label-position="inside">
                                    <template #label>City <span class="required-flag">*</span></template>
                                    <b-input required v-model="newUser.city"></b-input>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label-position="inside">
                                    <template #label>State <span class="required-flag">*</span></template>
                                    <b-select required v-model="newUser.state" expanded>
                                        <option value="AL">Alabama</option>
                                        <option value="AK">Alaska</option>
                                        <option value="AZ">Arizona</option>
                                        <option value="AR">Arkansas</option>
                                        <option value="CA">California</option>
                                        <option value="CO">Colorado</option>
                                        <option value="CT">Connecticut</option>
                                        <option value="DE">Delaware</option>
                                        <option value="DC">District Of Columbia</option>
                                        <option value="FL">Florida</option>
                                        <option value="GA">Georgia</option>
                                        <option value="HI">Hawaii</option>
                                        <option value="ID">Idaho</option>
                                        <option value="IL">Illinois</option>
                                        <option value="IN">Indiana</option>
                                        <option value="IA">Iowa</option>
                                        <option value="KS">Kansas</option>
                                        <option value="KY">Kentucky</option>
                                        <option value="LA">Louisiana</option>
                                        <option value="ME">Maine</option>
                                        <option value="MD">Maryland</option>
                                        <option value="MA">Massachusetts</option>
                                        <option value="MI">Michigan</option>
                                        <option value="MN">Minnesota</option>
                                        <option value="MS">Mississippi</option>
                                        <option value="MO">Missouri</option>
                                        <option value="MT">Montana</option>
                                        <option value="NE">Nebraska</option>
                                        <option value="NV">Nevada</option>
                                        <option value="NH">New Hampshire</option>
                                        <option value="NJ">New Jersey</option>
                                        <option value="NM">New Mexico</option>
                                        <option value="NY">New York</option>
                                        <option value="NC">North Carolina</option>
                                        <option value="ND">North Dakota</option>
                                        <option value="OH">Ohio</option>
                                        <option value="OK">Oklahoma</option>
                                        <option value="OR">Oregon</option>
                                        <option value="PA">Pennsylvania</option>
                                        <option value="RI">Rhode Island</option>
                                        <option value="SC">South Carolina</option>
                                        <option value="SD">South Dakota</option>
                                        <option value="TN">Tennessee</option>
                                        <option value="TX">Texas</option>
                                        <option value="UT">Utah</option>
                                        <option value="VT">Vermont</option>
                                        <option value="VA">Virginia</option>
                                        <option value="WA">Washington</option>
                                        <option value="WV">West Virginia</option>
                                        <option value="WI">Wisconsin</option>
                                        <option value="WY">Wyoming</option>
                                    </b-select>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label-position="inside">
                                    <template #label>Zip <span class="required-flag">*</span></template>
                                    <b-input required v-model="newUser.zip" v-mask="'######'" maxlength="5"></b-input>
                                </b-field>
                            </div>
                        </div>
                        <hr/>
                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                            <div class="column">
                                <b-field v-mask="'(###) ###-####'" label-position="inside">
                                    <template #label>Home (Cell) Phone <span class="required-flag">*</span></template>
                                    <b-input required v-model="newUser.phone"></b-input>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label="Work Phone" v-mask="'(###) ###-####'" label-position="inside">
                                    <b-input v-model="newUser.phone_2"></b-input>
                                </b-field>
                            </div>
                        </div>

                    </section>

                    <section class="content">
                        <p class="mt-6"><i class="fas fa-fw fa-lock-keyhole mr-3"></i>Login Information</p>

                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>

                            <div class="column">
                                <b-field label-position="inside">
                                    <template #label>Primary E-Mail Address <span class="required-flag">*</span></template>
                                    <b-input required type="email" v-model="newUser.email"></b-input>
                                </b-field>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>

                            <div class="column">
                                <b-field label="Secondary E-Mail Address" label-position="inside"
                                         :type="secondaryEmailMatchesPrimary ? 'is-danger' : ''"
                                         :message="secondaryEmailMatchesPrimary ? 'Your secondary e-mail address must be different from your primary e-mail address. If you do not have a second e-mail address, leave this field blank.' : ''">
                                    <b-input type="email" v-model="newUser.email_2"></b-input>
                                </b-field>
                                <p class="help">If you have a second email address enter it as an alternate. It can be used to login and lets us contact you in case your primary e-mail fails.</p>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>

                            <div class="column">
                                <b-field label-position="inside">
                                    <template #label>Password <span class="required-flag">*</span></template>

                                    <b-input required type="password" password-reveal v-model="newUser.password"></b-input>
                                </b-field>
                            </div>
                        </div>
                    </section>
                    
                    <section class="content mt-3">
                    </section>

                    <section class="content pb-5" v-if="newUser.state === 'WA'">
                        <hr />
                        <p class="mt-6"><i class="fas fa-fw fa-address-card mr-2"></i>Additional Information</p>

                        <div class="columns mt-3">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>

                            <div class="column">
                                <div class="mb-4 has-text-weight-bold">What best describes your position? <span class="required-flag">*</span></div>

                                <b-field>
                                    <b-radio v-model="newUser.cert_type" native-value="teacher" type="is-info">
                                        I have an OSPI teaching/admin/counselor certificate.
                                    </b-radio>
                                </b-field>
                                <b-field>
                                    <b-radio v-model="newUser.cert_type" native-value="paraeducator" type="is-info">
                                        I have an OSPI paraeducator certificate.
                                    </b-radio>
                                </b-field>
                                <b-field>
                                    <b-radio v-model="newUser.cert_type" native-value="coach" type="is-info">
                                        I am a community member volunteer/coach.
                                    </b-radio>
                                </b-field>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                            <div class="column">
                                <b-field label-position="inside">
                                    <template #label>District <span class="required-flag">*</span></template>
                                    <b-select v-model="newUser.district_id" required expanded>
                                        <option :value="0">Not Listed/Not Applicable</option>
                                        <option :value="999999">Private School</option>
                                        <option v-for="district in districts" v-if="district.id !== 9999999" :value="district.id">{{ district.name }} ({{ district.city | capitalize }})</option>
                                    </b-select>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label-position="inside">
                                    <template #label>School <span class="required-flag">*</span></template>
                                    <b-select v-model="newUser.school_id" required expanded>
                                        <option :value="0">Not Listed/Not Applicable</option>
                                        <option :value="school.id" v-for="school in schools">{{school.name}} ({{school.city | capitalize}})</option>
                                    </b-select>
                                </b-field>
                            </div>
                        </div>

                        <section v-if="newUser.cert_type && newUser.cert_type !== 'coach'">
                            <div class="columns">
                                <div class="column-is-narrow"><i class="far fa-fw"></i></div>

                                <div class="column is-8">
                                    <b-field label="Subject Area" label-position="inside">
                                        <template #label>Subject Area <span class="required-flag">*</span></template>
                                        <b-input v-model="newUser.subject_area"></b-input>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <b-field label-position="inside">
                                        <template #label>Grade Level <span class="required-flag">*</span></template>
                                        <b-input v-model="newUser.grade_level"></b-input>
                                    </b-field>
                                </div>
                            </div>

                            <div class="columns mt-3">
                                <div class="column-is-narrow"><i class="far fa-fw"></i></div>

                                <div class="column">
                                    <div class="mb-4 has-text-weight-bold">How did you hear about my247ed or who referred you?</div>

                                    <div v-for="r in referrals">
                                        <b-field>
                                            <b-checkbox v-model="newUser.referrals" :native-value="r" type="is-info">
                                                {{r}}
                                            </b-checkbox>
                                        </b-field>
                                    </div>

                                    <b-field label="Other/Details" label-position="inside" class="mt-4">
                                        <b-input type="text" maxlength="200" v-model="newUser.other_referral"></b-input>
                                    </b-field>

                                </div>
                            </div>
                        </section>

                    </section>
                    <hr class="mt-6"/>

                    <div class="columns is-gapless">
                        <div class="column is-narrow has-text-grey"><i class="fal fa-info-circle mr-3 ml-3"></i></div>
                        <div class="column is-size-7 has-text-grey pr-6">
                            By using our services, you certify that you are over 18 years of age. We may collect and share your data with trusted partners.
                        </div>
                        <div class="column has-text-right is-narrow ml-6">
                            <b-button type="is-primary" icon-right="arrow-right" :disabled="isDisabled" @click="formSubmit">Sign Up</b-button>
                        </div>
                    </div>
                    <section class="">

                    </section>
                </section>

                <section v-if="step===2">
                    <p class="mb-5 is-hidden-mobile">Based in your information we suggest these certifications or organizations to get started.</p>

                    <div v-if="newUser.cert_type !== 'coach'">
                        <hr/>

                        <div class="columns">
                            <div class="column is-narrow">
                                <i class="fad fa-certificate has-text-primary fa-2x"></i>
                            </div>
                            <div class="column">
                                <div class="has-text-weight-bold mb-3 is-size-5">
                                    Washington State Professional Development
                                </div>
                                <p class="has-text-grey">
                                    my247ed.com partners with many Washington State approved clock-hour providers for Washington State Professional Development opportunities. All courses and conferences found on our platform are guaranteed approved or accredited per all state requirements. </p>

                                <h4><i class="fas fa-diagram-project mr-2"></i>Integrations</h4>


                                <div class="mb-3">
                                    <strong>Washington OSPI Certification</strong> (<a href="https://www.k12.wa.us/certification" target="_blank">https://www.k12.wa.us/certification</a>)
                                </div>
                                <div class="columns">
                                    <div class="column is-4">
                                        <img src="@/assets/partner_assets/ospi.png">
                                    </div>
                                    <div class="column"></div>
                                    <div class="column is-5">
                                        <b-field label="OSPI Cert Number" label-position="inside">
                                            <b-input v-model="ospi.certification_number" placeholder="ie. 000000F"></b-input>
                                        </b-field>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <hr/>
                    <div class="columns mt-4">
                        <div class="column is-narrow">
                            <i class="fad fa-certificate has-text-primary fa-2x"></i>
                        </div>
                        <div class="column">
                            <div class="has-text-weight-bold mb-3 is-size-5">
                                Washington Interscholastic Activities Association (WIAA)
                            </div>
                            <p class="has-text-grey">
                                The Washington Interscholastic Activities Association (WIAA) is a private, nonprofit 501(c)(3) service organization and rule-making body that was formed in 1905 to create equitable playing conditions between high school sports teams in Washington.
                            </p>
                            <p class="mt-3"><a href="https://www.wiaa.com" target="_blank">https://www.wiaa.com</a></p>

                            <h4><i class="fas fa-diagram-project mr-2"></i>Integrations</h4>


                            <div class="mb-3">
                                <strong>Final Forms</strong> (<a href="https://www.finalforms.com" target="_blank">https://www.finalforms.com</a>)
                            </div>
                            <div class="columns">
                                <div class="column is-4">
                                    <img src="@/assets/partner_assets/finalforms.jpg">
                                </div>
                                <div class="column"></div>
                                <div class="column is-5">
                                    <b-field label="WIAA Membership ID" label-position="inside">
                                        <b-input v-model="wiaa.certification_number" placeholder="ie. WA-0000-0000"></b-input>
                                    </b-field>
                                    <div class="is-size-7"><a href="https://wiaa.finalforms.com/lookup_membership_id" target="_blank"><i class="fas fa-search mr-2"></i>Lookup WIAA Membership ID</a></div>
                                </div>
                            </div>

                        </div>
                    </div>

                    <hr class="mt-6"/>

                    <div class="columns">
                        <div class="column">
                            <b-button type="is-dark" size="is-small" outlined @click="updateCerts">Finish This Later...</b-button>
                        </div>
                        <div class="column has-text-right">
                            <b-button type="is-primary" icon-right="arrow-right" @click="updateCerts">Save & Login</b-button>
                        </div>
                    </div>

                </section>
            </div>
        </div>
    </div>
</template>

<script>
    import {client as http} from "../http_client";
    import {mapActions, mapGetters} from "vuex";
    import UiCard from "../components/UiCard";
    import GenderField from "../components/fields/GenderField";
    import {TITLE_OPTIONS} from "../constants/profile";

    export default {
        name: 'login',
        components: {UiCard, GenderField},
        data: function() {
            return {
                processing: false,
                newUser: {
                    first_name: null,
                    last_name: null,
                    title: null,
                    gender: null,
                    dob: null,
                    address: null,
                    address_2: null,
                    city: null,
                    state: null,
                    zip: null,
                    phone: null,
                    phone_2: null,
                    email: null,
                    email_2: null,
                    password: null,
                    district: null,
                    district_id: undefined,
                    school: null,
                    school_id: undefined,
                    subject_area: null,
                    grade_level: null,
                    cert_type: null,
                    referrals: [],
                    other_referral: null,
                },
                districts: [],
                schools: [],
                titleOptions: TITLE_OPTIONS,

                step: 1,
                prefilled: false,
                referral: 'a referring partner',

                ospi: {
                    certification_id: 1,
                    integration_id: 1,
                    certification_number: ''
                },
                wiaa: {
                    certification_id: 2,
                    integration_id: 2,
                    certification_number: ''
                },
                redirectTo: null,
                referrals: [
                    'Chad Mathison',
                    'John Twining',
                    'Rob Collins',
                    'Gus Montgomery',
                    'Lara Whitman',
                    'David Goldenkranz',
                    'Marah Walden',
                    'Tod Whitman',
                    'Washington Federation of Independent Schools (WFIS)',
                    'Washington Interscholastic Activities Association (WIAA)',
                    'Association of Washington Student Leaders (AWSL)',
                    'Washington School Counselor Association (WSCA)',
                    'Washington Secondary School Athletic Administration Association (WSSAAA)',
                    'Saint Georges School IB (SGS)',
                    'Conference or Event (Please specify below)',
                    'Facebook or other Social Network (Please provide referral below)',
                    'Friend or Colleague (Please provide referral below)',
                ]
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            secondaryEmailMatchesPrimary: function() {
                return !!(
                    this.newUser.email &&
                    this.newUser.email_2 &&
                    this.newUser.email.trim().toLowerCase() === this.newUser.email_2.trim().toLowerCase()
                );
            },
            isDisabled: function() {
                return (
                    this.processing ||
                    this.secondaryEmailMatchesPrimary ||
                    !this.newUser.first_name ||
                    !this.newUser.last_name ||
                    !this.newUser.dob ||
                    !this.newUser.address ||
                    !this.newUser.city ||
                    !this.newUser.state ||
                    !this.newUser.zip ||
                    !this.newUser.phone ||
                    !this.newUser.email ||
                    !this.newUser.password ||
                    (
                        this.newUser.state === 'WA' && (
                            !this.newUser.cert_type ||
                            !this.newUser.school ||
                            this.newUser.school_id === undefined ||
                            !this.newUser.district ||
                            this.newUser.district_id === undefined ||
                            (this.newUser.cert_type !== 'coach' && (!this.newUser.subject_area || !this.newUser.grade_level))
                        )
                    )
                );
            }
        },
        methods: {
            ...mapActions(['login']),
            formSubmit() {
                if (!this.processing) {
                    this.processing = true;
                    // ensure district id and name are equal
                    if (this.newUser.district_id === 999999) {
                        this.newUser.district_id = null;
                        this.newUser.district = 'Private School';
                    } else if (!this.newUser.district_id) {
                        this.newUser.district_id = null;
                        this.newUser.district = 'Not Listed/Not Applicable';
                    } else {
                        this.newUser.district = this.districts.find(d => d.id === this.newUser.district_id).name;
                    }
                    // ensure school id and name are equal
                    if (!this.newUser.school_id) {
                        this.newUser.school_id = null;
                        this.newUser.school = 'Not Listed/Not Applicable';
                    } else {
                        this.newUser.school = this.schools.find(s => s.id === this.newUser.school_id).name;
                    }


                    this.newUser.referral = this.newUser.referrals.join(', ');
                    if (this.newUser.other_referral) {
                        this.newUser.referral += ', ' + this.newUser.other_referral;
                    }

                    let payload = {...this.newUser};
                    delete payload.referrals;
                    delete payload.other_referral;

                    http.post('app/users', payload, {force: true}).then(response => {
                        this.login({username: this.newUser.email, password: this.newUser.password}).catch(err => {
                            if (err && err.message) {
                                this.$notify('error', 'Login Error', err.message, {duration: 5000, permanent: false});
                            } else if (err && err.error && err.error.text) {
                                this.$notify('error', 'Login Error', err.error.text, {duration: 5000, permanent: false});
                            } else if (err) {
                                this.$notify('error', 'Login Error', 'An unknown error occurred. Please try again later.', {duration: 5000, permanent: false});
                            }

                            setTimeout(() => {
                                this.$router.push('/');
                            }, 2000);
                        });
                    }).catch(err => {
                        if (err) console.log(err.response);
                        window.alert(err.response.data + ' If you need additional help please contact us at support@my247ed.com.');
                        this.processing = false;
                    });
                }
            },
            updateCerts() {
                http.put('app/users/certifications/' + this.ospi.certification_id, this.ospi).then(response => {

                });

                localStorage.setItem('wiaa_id', this.wiaa.certification_number);
                http.put('app/users/certifications/' + this.wiaa.certification_id, this.wiaa).then(response => {

                });

                if (this.$route.query.return === 'www') {
                    window.location.href = 'https://www.my247ed.com/account/login.php';
                } else {
                    localStorage.removeItem('redirectTo');
                    this.$router.push((this.redirectTo) ? this.redirectTo : '/dashboard');
                }
            },
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                }
            },
        },
        watch: {
            currentUser(val) {
                if (val && val.id) {
                    http.put('app/users/certifications/' + this.wiaa.certification_id, this.wiaa).then(response => {

                    });
                    this.scrollTo('signup-frame');
                    this.step = 2;
                }
            },
            'newUser.district_id'(val) {
                this.schools = [];
                if (val === 0)
                    this.newUser.district = 'Not Listed/Not Applicable';
                else if (val === 999999)
                    this.newUser.district = 'Private School';
                else {
                    let code;
                    this.districts.forEach(d => {
                        if (d.id === val) {
                            this.newUser.district = d.name;
                            code = d.code;
                        }
                    });
                    http.get('districts/schoolsIn/' + code).then(results => {
                        this.schools = results.data;
                    }).catch(err => {
                        console.error(err);
                        this.schools = [
                            {
                                name: 'Error retrieving schools',
                                code: 9999999,
                                city: '',
                            }
                        ]
                    });
                }
                this.newUser.school_id = undefined;
                this.newUser.school = null;
            },
            'newUser.school_id'(val) {
                if (val === 0 || val === 999999)
                    this.newUser.school = 'Not Listed/Not Applicable';
                else
                    this.schools.forEach(s => {
                        if (s.id === val)
                            this.newUser.school = s.name;
                    });
            },
        },
        mounted() {
            let remember = localStorage.getItem('remember');
            if (remember) {
                remember = JSON.parse(remember);
                this.newUser.email = remember.email;
                this.newUser.email_2 = remember.email_2;
                this.newUser.first_name = remember.first_name;
                this.newUser.last_name = remember.last_name;
                this.newUser.dob = remember.dob;
                this.newUser.address = remember.address;
                this.newUser.address_2 = remember.address_2;
                this.newUser.city = remember.city;
                this.newUser.state = remember.state;
                this.newUser.zip = remember.zip;
                this.newUser.phone_2 = remember.phone_2;
                this.newUser.phone = remember.phone;
                this.newUser.district = remember.district;

                this.wiaa.certification_number = remember.wiaa_id;

                this.prefilled = true;
                this.referral = (remember.referral) ? remember.referral : 'a referring partner';
            }

            if (localStorage.getItem('redirectTo')) {
                this.redirectTo = localStorage.getItem('redirectTo');
            }

            http.get('districts').then(results => {
                this.districts = results.data;
            }).catch(err => {
                console.error(err);
                this.districts = [
                    {
                        name: 'Error retrieving districts',
                        code: 9999999,
                        city: '',
                    }
                ]
            });
        }
    };
</script>

<style lang="scss">
    #signup-frame {
        display: flex;
        min-height: 100vh;
        align-items: center;
        justify-content: center;
        background: #224C74 radial-gradient(#336699, #1e385c);

        .auth-card {
            margin: 4rem 2rem 8rem;
            border-radius: 8px;
            box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.3);

            .left-side {
                background-color: #FFFFFF;
                padding: 4rem 3.5rem 4rem 3.5rem;
                border-radius: 5px;
                color: #666666;

                strong {
                    color: #484848;
                }

                h1 {
                    font-weight: normal;
                    font-size: 2.6rem !important;
                    color: #282828;
                    margin-bottom: 1.5rem;
                    letter-spacing: -1px;

                    strong {
                        color: #336699;
                        font-weight: 900;
                    }

                    @media (max-width: 768px) {
                        font-size: 2.2rem !important;
                        margin-bottom: 1rem;
                    }
                }

                > p {
                    font-size: 1.2rem;
                    line-height: 1.7rem;
                }

                .is-size-7 {
                    font-size: 0.85rem !important;
                }

                .signup {
                    border-top: 1px solid #e9e9e9;
                    padding-top: 1.5rem;
                    margin-top: 4rem;

                    .button {
                        padding-left: 1.3rem;
                    }

                    @media (max-width: 768px) {
                        margin-top: 2rem;
                    }
                }

                @media (max-width: 768px) {
                    border-radius: 5px;
                    padding: 3rem 1rem 2.5rem 1rem;
                    box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.3);
                }
            }

            .right-side {
                background-color: #336699;
                border-radius: 0 5px 5px 0;
                padding: 0;
                position: relative;

                color: white;

                section {
                    padding: 1rem 2rem 0 4rem;
                    color: rgba(255, 255, 255, 0.8);
                    line-height: 1.4rem;

                    &.header {
                        padding: 8.8rem 2rem 0 4rem;
                        line-height: 1.9rem;
                        color: white;

                        h1 {
                            font-size: 2.4rem;
                            font-weight: 900;
                        }

                        h2 {
                            font-size: 1.4rem;
                            font-weight: normal;
                        }
                    }
                }

                img {
                    border-radius: 0 5px 5px 0;
                    position: absolute;
                    bottom: 0;
                    padding: 0;
                }
            }

            h4 {
                font-size: 1.2rem;
                margin-top: 1.5rem;
                margin-bottom: 2rem;
                padding-bottom: 0.8rem;

                i {
                    color: #336699;
                }

                border-bottom: 1px dashed #ddd;
            }

            @media (max-width: 768px) {
                margin: 1rem;
                box-shadow: none;
            }
        }

        @media (max-width: 768px) {
            align-items: normal;
            justify-content: normal;
        }

        span {
            &.beta {
                color: #333;
                border: 1px solid #c2c2c2;
                border-radius: 3px;
                padding: 0 4px;
                font-size: 0.6rem;
                margin-left: 0.5rem;
                vertical-align: super;
                font-weight: normal;
                letter-spacing: normal;
            }
        }
    }

    .required-flag {
        color: red;
    }
</style>
